import React, { useCallback } from "react"
import MuiAutocomplete from "@mui/material/Autocomplete"
import TextField from "@mui/material/TextField"
import Box from "@mui/material/Box"
import { filter, isEqual, map, some } from "lodash"
import { Chip, Tooltip } from "@mui/material"
import CheckIcon from "@mui/icons-material/Check"

interface AutocompleteFilterProps<T> {
  title: string
  field: string
  value: any
  optionKey: string
  collection: Array<T>
  optionLabel: (item: T) => string
  optionValue: (item: T) => any
  isDisabled?: (item: T) => boolean
  onChange: ({ field, value }) => void
}

export default function PipelineAutocompleteFilterMulti<T extends any>(
  props: AutocompleteFilterProps<T>
) {
  const {
    title,
    field,
    value,
    collection,
    optionLabel,
    optionValue,
    isDisabled,
    onChange,
    optionKey,
  } = props

  const getSelectedValues = () => {
    if (!value || !Array.isArray(value)) return []
    return filter(collection, (item) => {
      const itemValue = optionValue(item)
      return some(value, (v) => isEqual(v, itemValue))
    })
  }

  const handleChange = (_: any, newValue: T[] | null, action: string, option) => {
    const isAllSelected = option?.option === "All" || option?.option?.["name"] === "All"
    const hasAll = some(newValue, (value) => value === "All" || value?.["name"] === "All")
    if (action === "removeOption" && isAllSelected) {
      return onChange({ field, value: [] })
    }

    if (action === "removeOption" && hasAll) {
      return onChange({
        field,
        value:
          newValue
            ?.filter((value) => value != "All" && value?.["name"] != "All")
            .map(optionValue) || [],
      })
    }

    if (action === "selectOption" && isAllSelected) {
      return onChange({
        field,
        value: map(
          filter(collection, (item) => !item["disable"]),
          optionValue
        ),
      })
    }

    onChange({ field, value: newValue?.map(optionValue) || [] })
  }

  const showValueToottip = () => {
    const filteredValue = value?.filter((value) => value != "All" && value?.[optionKey] != "All")
    if (value?.length > 1) {
      return filteredValue?.[0]?.[optionKey]
        ? filteredValue.map((value) => value?.[optionKey]).join(",")
        : filteredValue.join(",")
    }

    return ""
  }

  const truncateLabel = (label: string, maxLength: number) => {
    if (value?.length) {
      return label.length > maxLength ? label.slice(0, maxLength) + "…" : label
    }
    return label
  }

  return (
    <Tooltip title={showValueToottip()} placement="top">
      <Box>
        <MuiAutocomplete
          id=""
          multiple
          value={getSelectedValues()}
          onChange={handleChange}
          options={collection}
          getOptionLabel={optionLabel}
          getOptionDisabled={isDisabled}
          defaultValue={[]}
          isOptionEqualToValue={(option, value) => {
            return isEqual(optionValue(option), optionValue(value))
          }}
          renderInput={(params) => {
            return <TextField {...params} label={title} size="small" variant="outlined" />
          }}
          renderTags={(value: readonly T[], getTagProps) => {
            const maxDisplay = 1
            const cleanedValue = value.filter((value) =>
              typeof value === "string" ? value != "All" : value["name"] != "All"
            )

            const displayedItems = cleanedValue.slice(0, maxDisplay)

            const remainingCount = cleanedValue.length - maxDisplay
            return (
              <>
                {displayedItems.map((option: T, index: number) => {
                  const { key, ...tagProps } = getTagProps({
                    index: value.length != cleanedValue.length ? index + 1 : index,
                  })
                  return (
                    <Chip
                      key={key}
                      variant="outlined"
                      label={truncateLabel(optionLabel(option), 5)}
                      size="small"
                      {...tagProps}
                    />
                  )
                })}
                {remainingCount > 0 && (
                  <Chip
                    variant="outlined"
                    label={`+${remainingCount} more`}
                    size="small"
                    disabled
                  />
                )}
              </>
            )
          }}
          renderOption={(props, option, { selected }) => {
            return (
              <li {...props}>
                <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                  <Box sx={{ flexGrow: 1 }}>{optionLabel(option)}</Box>
                  {selected && <CheckIcon fontSize="small" color="primary" />}
                </Box>
              </li>
            )
          }}
          fullWidth
        />
      </Box>
    </Tooltip>
  )
}
